import React, { useEffect } from 'react';
import { useSpeechRecognition } from './useSpeechRecognition';

/* ─── Animaciones CSS ────────────────────────────────────────────────────── */
const VOICE_CSS = `
@keyframes voice-ring {
    0%   { transform: scale(1);   opacity: 0.55; }
    100% { transform: scale(2.4); opacity: 0;    }
}
@keyframes voice-eq {
    0%, 100% { height: 4px;  }
    25%       { height: 20px; }
    50%       { height: 28px; }
    75%       { height: 10px; }
}
@keyframes voice-dot {
    0%, 80%, 100% { transform: scale(0.45); opacity: 0.3; }
    40%           { transform: scale(1);    opacity: 1;   }
}
`;

const EQ_BARS = [
    { delay: '0s',     dur: '0.52s' },
    { delay: '0.09s',  dur: '0.44s' },
    { delay: '0.18s',  dur: '0.64s' },
    { delay: '0.04s',  dur: '0.48s' },
    { delay: '0.22s',  dur: '0.58s' },
    { delay: '0.12s',  dur: '0.41s' },
    { delay: '0.16s',  dur: '0.56s' },
];

/**
 * VoiceRecorder
 *
 * Estados visuales (8):
 *   idle · listening · transcribing · processing · completed · stopped · timeout · error
 *
 * Props:
 *   onTranscript(text)       — texto final a anexar al textarea
 *   onActiveChange(boolean)  — true mientras la sesión está activa (glow en textarea)
 */
export function VoiceRecorder({ onTranscript, onActiveChange }) {
    const {
        status,
        isRunning,
        interimTranscript,
        confidence,
        error,
        isSupported,
        start,
        stop,
    } = useSpeechRecognition({
        onFinalResult: (text) => onTranscript?.(text),
        silenceTimeout: 60_000, // 1 minuto sin voz → detener automáticamente
    });

    // Estados de animación activa (solo los de "escuchando de verdad")
    const isActive = ['listening', 'transcribing', 'processing'].includes(status);

    // Notificar al padre: glow mientras la sesión esté viva (incluye 'completed')
    useEffect(() => {
        onActiveChange?.(isRunning);
    }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isSupported) return null;

    // Toggle correcto: usa isRunning (sesión real) no isActive (visual)
    // Evita el bug donde status='completed' pero la sesión sigue activa
    const handleToggle = () => (isRunning ? stop() : start());

    // ── Estilos del botón según estado ───────────────────────────────────────
    const buttonCls = (() => {
        const base = 'relative flex items-center gap-2.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2';
        if (isActive)             return `${base} bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-200/70 focus:ring-red-400`;
        if (status === 'completed') return `${base} bg-green-100 hover:bg-green-200 text-green-700 focus:ring-green-400`;
        if (status === 'stopped')   return `${base} bg-slate-200 text-slate-600 focus:ring-slate-400`;
        if (status === 'timeout')   return `${base} bg-amber-100 hover:bg-amber-200 text-amber-700 focus:ring-amber-400`;
        return `${base} bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-600 focus:ring-blue-400`;
    })();

    // ── Etiqueta del botón ────────────────────────────────────────────────────
    const buttonLabel = {
        idle:         'Dictar observación',
        listening:    'Escuchando...',
        transcribing: 'Capturando voz...',
        processing:   'Procesando...',
        completed:    '✓ Texto agregado',
        stopped:      'Dictado detenido',
        timeout:      '⏱ Detenido por inactividad',
        error:        'Dictar observación',
    }[status] ?? 'Dictar observación';

    // ── Chip de confianza ─────────────────────────────────────────────────────
    const confidenceChip =
        confidence === null ? null :
        confidence > 0.85 ? { label: `Alta precisión · ${Math.round(confidence * 100)}%`, cls: 'bg-green-100 text-green-700' } :
        confidence > 0.6  ? { label: `Precisión media · ${Math.round(confidence * 100)}%`, cls: 'bg-yellow-100 text-yellow-700' } :
                            { label: `Baja precisión · ${Math.round(confidence * 100)}%`, cls: 'bg-red-100 text-red-700' };

    return (
        <div className="mt-3 space-y-2.5 select-none" aria-label="Control de dictado por voz">
            <style>{VOICE_CSS}</style>

            {/* ── Fila principal: botón + indicadores ───────────────────────── */}
            <div className="flex items-center gap-3 flex-wrap">

                {/* Botón con anillos expansivos cuando está activo */}
                <div className="relative">
                    {isActive && (
                        <>
                            <span className="absolute inset-0 rounded-full bg-red-400"
                                  style={{ animation: 'voice-ring 1.5s ease-out infinite' }} />
                            <span className="absolute inset-0 rounded-full bg-red-400"
                                  style={{ animation: 'voice-ring 1.5s ease-out infinite 0.55s' }} />
                        </>
                    )}

                    <button
                        type="button"
                        onClick={handleToggle}
                        aria-label={isRunning ? 'Detener dictado' : 'Iniciar dictado de observaciones'}
                        title={isRunning ? 'Clic para detener' : 'Clic para iniciar dictado'}
                        className={buttonCls}
                    >
                        {/* Ícono micrófono */}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                             strokeWidth={2} stroke="currentColor"
                             className={`w-4 h-4 ${isActive ? 'animate-pulse' : ''}`}
                             aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                        </svg>

                        <span>{buttonLabel}</span>

                        {/* Punto luminoso cuando está escuchando */}
                        {isActive && (
                            <span className="w-2 h-2 bg-white rounded-full opacity-90 animate-pulse"
                                  aria-hidden="true" />
                        )}
                    </button>
                </div>

                {/* ── Ecualizador (transcribing) ─────────────────────── */}
                {status === 'transcribing' && (
                    <div className="flex items-end gap-0.5" style={{ height: '28px' }} aria-hidden="true">
                        {EQ_BARS.map((bar, i) => (
                            <div
                                key={i}
                                className="w-1.5 bg-red-400 rounded-full"
                                style={{
                                    height: '4px',
                                    animation: `voice-eq ${bar.dur} ease-in-out infinite`,
                                    animationDelay: bar.delay,
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* ── Puntos de procesamiento (processing) ──────────── */}
                {status === 'processing' && (
                    <div className="flex items-center gap-1.5" aria-hidden="true">
                        {[0, 1, 2].map(i => (
                            <div
                                key={i}
                                className="w-2 h-2 bg-blue-400 rounded-full"
                                style={{
                                    animation: 'voice-dot 1.3s ease-in-out infinite',
                                    animationDelay: `${i * 0.2}s`,
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* ── Chip de confianza (completed) ─────────────────── */}
                {status === 'completed' && confidenceChip && (
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${confidenceChip.cls}`}>
                        {confidenceChip.label}
                    </span>
                )}
            </div>

            {/* ── Transcripción parcial en tiempo real ──────────────────────── */}
            {(status === 'transcribing' || status === 'processing') && interimTranscript && (
                <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                     aria-live="polite" aria-atomic="false">
                    <p className="font-bold text-gray-400 uppercase tracking-widest text-[10px] mb-1">
                        Transcribiendo en tiempo real:
                    </p>
                    <p className="text-gray-700 italic leading-relaxed">"{interimTranscript}"</p>
                </div>
            )}

            {/* ── Aviso de inactividad (timeout) ────────────────────────────── */}
            {status === 'timeout' && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700"
                     role="status" aria-live="polite">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    <span>Dictado detenido por inactividad (1 min sin voz). Haz clic para volver a dictar.</span>
                </div>
            )}

            {/* ── Error ─────────────────────────────────────────────────────── */}
            {status === 'error' && error && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700"
                     role="alert" aria-live="assertive">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         strokeWidth={1.5} stroke="currentColor"
                         className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                    </svg>
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
}
